import { FC, useEffect, useCallback, useState, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { animated, useTransition } from '@react-spring/web';
import { Route, Routes, useLocation, useNavigate } from 'react-router-dom';
import classNames from 'classnames';
import { firebaseAuthObj, createAuthData, getUserInfo } from 'lib/firebase';
import { loginSuccessful, StoredUser } from 'store/features/authentication';
import {
    menuBarOff,
    viewAnimationOff,
    viewAnimationOn,
} from 'store/features/ui';
import { RootState } from 'store';
import {
    Billing,
    Inventory,
    Landing,
    Monitors,
    Organization,
    Team,
    Planning,
    Profile,
    Suppliers,
    Accounts,
    Vehicles,
    Consumers,
} from 'views';
import styles from './MainContent.module.scss';
import { DeviceDetails } from 'views/DeviceDetails/DeviceDetails';
import { SupplierDetails } from 'views/Suppliers/SupplierDetails/SupplierDetails';
import { Efficiency } from 'views/Efficiency/Efficiency';
import { LiveMonitoring } from 'views/LiveMonitoring/LiveMonitoring';
import { Issues } from 'views/Issues/Issues';
import { Map } from 'views/Map/Map';
import { setSearch } from 'store/features/globalSearch';
import { ContactSupportAccountCreationDialog } from 'views/Landing/components/ContactSupportAccountCreationDialog/ContactSupportAccountCreationDialog';
import { useAuth } from 'hooks';
import { IdTokenResult } from 'firebase/auth';
import { FirebaseUser } from 'types/User/firebaseTypes';
import { TermsOfServiceModal } from 'components/modals/TermsOfServiceModal/TermsOfServiceModal';
import { NewUserInvite } from 'views/Invite/Invite';
import { ConsumerDetails } from 'views/Consumers/ConsumerDetails/ConsumerDetails';

const routeOrder = [
    '/',
    '/live-reporting',
    '/accounts',
    '/efficiency',
    '/efficiency/trends',
    '/efficiency/accounts',
    '/planning/list',
    '/planning/day',
    '/planning/week',
    '/consumers',
    '/billing',
    '/inventory',
    '/issues',
    '/monitors/overview',
    '/monitors/shipments',
    '/monitors/summary',
    '/map',
    '/team',
    '/vehicles',
    '/organization',
    '/suppliers',
    '/supplier-details',
    '/profile',
    '/invite',
];
const fromState = { opacity: 0, transform: 'translate3d(0, 100vh, 0)' };
const enterState = { opacity: 1, transform: 'translate3d(0, 0vh, 0)' };
const leaveState = { opacity: 0, transform: 'translate3d(0, -50vh, 0)' };
const fromStateBackwards = {
    opacity: 0,
    transform: 'translate3d(0, -100vh, 0)',
};
const enterStateBackwards = {
    opacity: 1,
    transform: 'translate3d(0, 0vh, 0)',
};
const leaveStateBackwards = {
    opacity: 0,
    transform: 'translate3d(0, 50vh, 0)',
};
const fromStateLogin = { opacity: 0 };
const enterStateLogin = { opacity: 1 };
const leaveStateLogin = { opacity: 0 };

declare global {
    interface Window {
        Appcues: Appcues;
    }
}

interface Appcues {
    identify(
        id: string | undefined,
        properties: {
            email?: string;
            companyName: string;
            planType: string;
            planTier: string;
            role: string;
        }
    ): void;
}

const MainContent: FC = () => {
    const location = useLocation();
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const ui = useSelector((state: RootState) => state.ui);
    const authentication = useSelector(
        (state: RootState) => state.authentication
    );
    const { user } = useAuth();
    const [userInfo, setUserInfo] = useState<FirebaseUser['info']>();

    const [lastPath, setLastPath] = useState('/');
    const [contactSupportDialogOpen, setContactSupportDialogOpen] =
        useState(false);
    const [tosDialogOpen, setTosDialogOpen] = useState(false);

    const isPathIncremented = useCallback(() => {
        if (
            routeOrder.indexOf(lastPath) < routeOrder.indexOf(location.pathname)
        )
            return true;
        return false;
    }, [location.pathname, lastPath]);

    const transitionRef = useRef(null);

    const getFromTransition = useCallback(() => {
        if (lastPath === '/' || location.pathname === '/')
            return fromStateLogin;
        if (isPathIncremented()) return fromState;
        return fromStateBackwards;
    }, [location.pathname, lastPath, isPathIncremented]);

    const getEnterTransition = useCallback(() => {
        if (lastPath === '/' || location.pathname === '/')
            return enterStateLogin;
        if (isPathIncremented()) return enterState;
        return enterStateBackwards;
    }, [location.pathname, lastPath, isPathIncremented]);

    const getLeaveTransition = useCallback(() => {
        if (lastPath === '/' || location.pathname === '/')
            return leaveStateLogin;
        if (isPathIncremented()) return leaveState;
        return leaveStateBackwards;
    }, [location.pathname, lastPath, isPathIncremented]);

    const getClassNames = useCallback(() => {
        if (location.pathname === '/') {
            return styles['landing-view'];
        }
        if (ui.isMobile) {
            return classNames(styles.content, styles.isMobile);
        }
        return styles.content;
    }, [location.pathname, ui.isMobile]);

    const transitions = useTransition([location.pathname], {
        from: getFromTransition,
        enter: getEnterTransition,
        leave: getLeaveTransition,
        onDestroyed: () => {
            setLastPath(location.pathname);
        },
        onStart: () => {
            document.body.classList.add('animating');
            dispatch(viewAnimationOn());
        },
        onRest: () => {
            document.body.classList.remove('animating');
            dispatch(viewAnimationOff());
        },
    });

    useEffect(() => {
        const unregisterIdTokenChangedObserver =
            firebaseAuthObj?.onIdTokenChanged((user: any) => {
                if (!!user && !authentication.user) {
                    createAuthData(user).then((storedUser: StoredUser) => {
                        dispatch(loginSuccessful(storedUser));
                    });
                } else if (
                    !user &&
                    location.pathname !== '/' &&
                    !location.pathname.includes('invite')
                )
                    navigate('/');
            });

        return () => {
            if (!!unregisterIdTokenChangedObserver)
                unregisterIdTokenChangedObserver();
        };
    }, [authentication.user, dispatch, location.pathname, navigate]);

    useEffect(() => {
        if (user?.uid) {
            getUserInfo(user?.uid).then((info) => setUserInfo(info));
        }
    }, [user, tosDialogOpen]);

    const handleResize = useCallback(() => {
        if (ui.isMobile) {
            const vh = window.innerHeight / 100;

            document.documentElement.style.setProperty('--vh', `${vh}px`);
        }
    }, [ui.isMobile]);

    useEffect(() => {
        if (ui.isMobile) {
            dispatch(menuBarOff());
            const vh = window.innerHeight / 100;

            document.documentElement.style.setProperty('--vh', `${vh}px`);
            document.addEventListener('resize', handleResize);

            return () => document.removeEventListener('resize', handleResize);
        }
    }, [location.pathname, ui.isMobile, dispatch, handleResize]);

    useEffect(() => {
        if (!ui.animatingViewTransition) {
            if (transitionRef?.current) {
                (transitionRef.current as HTMLElement).setAttribute(
                    'style',
                    'opacity: 1;'
                );
            }
        }
    }, [ui.animatingViewTransition]);

    // Clear search when user navigates to a new page.
    useEffect(() => {
        dispatch(setSearch(''));
    }, [dispatch, location]);

    const setAppcuesIdentify = useCallback(
        (uId: string, uEmail: string, uSupplierId: string, uTos: boolean) => {
            if (uId && uEmail && uSupplierId && uTos) {
                window.Appcues.identify(uId, {
                    email: uEmail,
                    companyName: uSupplierId,
                    planType: 'Trial',
                    planTier: 'Standard',
                    role: 'Admin',
                });
            }
        },
        []
    );

    // Check validity of initial user info (i.e., supplierId + tos acceptance). Show dialogs as appropriate.
    useEffect(() => {
        firebaseAuthObj?.currentUser
            ?.getIdTokenResult(true)
            .then((result: IdTokenResult) => {
                if (!result.claims.supplierId?.toString()) {
                    setContactSupportDialogOpen(true);
                }
                // Next, check if the user has accepted the TOS, and if not, prompt them to.
                else if (
                    userInfo &&
                    !userInfo.tos &&
                    !!userInfo?.lastLoginPortal
                ) {
                    setTosDialogOpen(true);
                } else {
                    setAppcuesIdentify(
                        user?.uid || '',
                        user?.email || '',
                        result.claims.supplierId.toString(),
                        !!userInfo?.tos
                    );
                }
            });
    }, [user, authentication, userInfo, setAppcuesIdentify]);

    return (
        <div data-testid="MainContent">
            {transitions((props, item) => (
                <animated.div style={props} ref={transitionRef}>
                    <div className={getClassNames()}>
                        <Routes location={item}>
                            <Route path="/" element={<Landing />} />
                            <Route path="/billing" element={<Billing />} />
                            <Route path="/inventory" element={<Inventory />} />
                            <Route
                                path="/live-reporting"
                                element={<LiveMonitoring />}
                            />
                            <Route path="/accounts" element={<Accounts />} />
                            <Route
                                path="/live-reporting/:tabName"
                                element={<LiveMonitoring />}
                            />
                            <Route
                                path="/efficiency/:tabName"
                                element={<Efficiency />}
                            />
                            <Route
                                path="/planning/:tabName"
                                element={<Planning />}
                            />
                            <Route path="/consumers" element={<Consumers />} />
                            <Route
                                path="/consumer-details/:id"
                                element={<ConsumerDetails />}
                            />
                            <Route path="/issues" element={<Issues />} />
                            <Route
                                path="/monitors/:tabName"
                                element={<Monitors />}
                            />
                            <Route path="/team" element={<Team />} />
                            <Route path="/vehicles" element={<Vehicles />} />
                            <Route
                                path="/organization"
                                element={<Organization />}
                            />
                            <Route
                                path="/organization/:organizationTabName"
                                element={<Organization />}
                            />
                            <Route path="/map" element={<Map />} />
                            <Route path="/profile" element={<Profile />} />
                            <Route
                                path="/device-details/:id/:tabName"
                                element={<DeviceDetails />}
                            />
                            <Route
                                path="/device-details/:id"
                                element={<DeviceDetails />}
                            />
                            <Route path="/suppliers" element={<Suppliers />} />
                            <Route
                                path="/supplier-details/:id"
                                element={<SupplierDetails />}
                            />
                            <Route
                                path="supplier-details/:id/:supplierTabName"
                                element={<SupplierDetails />}
                            />
                            <Route
                                path="supplier-details/:id/:supplierTabName/:secondaryTabName"
                                element={<SupplierDetails />}
                            />
                            <Route
                                path="invite/:inviteCode"
                                element={
                                    <NewUserInvite
                                        setTosDialogOpen={setTosDialogOpen}
                                    />
                                }
                            />
                        </Routes>
                    </div>
                </animated.div>
            ))}

            <ContactSupportAccountCreationDialog
                open={contactSupportDialogOpen}
                setOpen={setContactSupportDialogOpen}
            />

            <TermsOfServiceModal
                modalOpen={tosDialogOpen}
                setModalOpen={setTosDialogOpen}
            />
        </div>
    );
};

export default MainContent;
